import { Text, type TextProps } from '@frontend/web-react';
import { useTypedText } from '@frontend/web/hooks';
import React from 'react';

type TypedTextProps = Omit<TextProps, 'children'> & {
  backSpeed?: number;
  fixedText?: string;
  loop?: boolean;
  playOnce?: boolean;
  showCursor?: boolean;
  startDelay?: number;
  strings: string[];
  typeSpeed?: number;
};

export const TypedText: React.FC<TypedTextProps> = ({
  backSpeed = 30,
  fixedText,
  loop = true,
  playOnce = false,
  showCursor = true,
  startDelay = 0,
  strings,
  typeSpeed = 40,
  ...textProps
}) => {
  const { displayText, finalText, hasPlayedOnce, hasStarted, shouldShowCursor } = useTypedText({
    backSpeed,
    fixedText,
    loop,
    playOnce,
    showCursor,
    startDelay,
    strings,
    typeSpeed,
  });

  // Don't render anything until the start delay has passed (unless animation already played)
  if (!hasStarted && !hasPlayedOnce) {
    return null;
  }

  // If animation has already played once, show the final text immediately
  if (hasPlayedOnce && playOnce) {
    return (
      <Text {...textProps}>
        {fixedText ? `${fixedText} ` : null}
        {finalText}
      </Text>
    );
  }

  // Don't render if there's no content to display yet
  if (!displayText && !fixedText) {
    return null;
  }

  return (
    <Text {...textProps}>
      {fixedText ? `${fixedText} ` : null}
      {displayText}
      {shouldShowCursor ? '|' : null}
    </Text>
  );
};
